"use client";

import { usePathname, useRouter } from "next/navigation";
import { createContext, useContext } from "react";
import type { ReactElement, ReactNode } from "react";
import { type CareersLang, careersTogglePath } from "./careers-constants";

export type { CareersLang };

const CareersLanguageContext = createContext<{
  readonly lang: CareersLang;
  readonly toggle: () => void;
} | null>(null);

/**
 * Language for the main careers overview.
 * URL is source of truth: `/careers` = DE, `/careers/en` = EN.
 * `/careers/engineering` and `/careers/developers` are English-only.
 */
export function CareersLanguageProvider({
  children,
  initialLang = "de",
}: {
  readonly children: ReactNode;
  readonly initialLang?: CareersLang;
}): ReactElement {
  const router = useRouter();
  const pathname = usePathname();

  const toggle = (): void => {
    const hash: string =
      typeof window !== "undefined" ? window.location.hash : "";
    router.push(`${careersTogglePath(pathname)}${hash}`);
  };

  return (
    <CareersLanguageContext.Provider value={{ lang: initialLang, toggle }}>
      {children}
    </CareersLanguageContext.Provider>
  );
}

export function useCareersLanguage(): {
  readonly lang: CareersLang;
  readonly toggle: () => void;
} {
  const ctx = useContext(CareersLanguageContext);
  if (!ctx)
    throw new Error(
      "useCareersLanguage must be used within CareersLanguageProvider",
    );
  return ctx;
}
